import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X } from 'lucide-react'

export type ConfirmTone = 'danger' | 'warning' | 'info'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  onConfirm: () => void
  onCancel: () => void
}

const toneStyles: Record<ConfirmTone, { border: string; icon: string; button: string }> = {
  danger: {
    border: 'border-red-500/40',
    icon: 'text-red-400',
    button: 'bg-red-600 hover:bg-red-500 text-white',
  },
  warning: {
    border: 'border-amber-500/40',
    icon: 'text-amber-400',
    button: 'bg-amber-600 hover:bg-amber-500 text-white',
  },
  info: {
    border: 'border-blue-500/40',
    icon: 'text-blue-400',
    button: 'bg-blue-600 hover:bg-blue-500 text-white',
  },
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
      if (e.key === 'Enter') onConfirm()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onCancel, onConfirm])

  if (!open) return null

  const style = toneStyles[tone]

  // Render to document.body via a portal. The DataBackup component lives
  // inside Header, which uses `backdrop-blur` — and `backdrop-filter` on
  // an ancestor creates a containing block for `position: fixed`
  // descendants. That meant `fixed inset-0` was being sized to the 56px
  // header bar, not the viewport, and any dialog content past the first
  // ~56px got cut off above-viewport with no way to scroll to it. v0.8.13
  // tried `max-h` + flex centering; v0.8.14 (first attempt) tried a
  // scroll wrapper. Both were correct CSS for the wrong premise. Portaling
  // to body sidesteps the header's containing block entirely.
  //
  // **Layout once portaled.** Fixed outer covers the viewport and scrolls
  // when content is taller than it. Middle div is `min-h-full flex
  // items-center` so the card centers when it fits and the scroll
  // gracefully takes over when it doesn't (top of the card stays visible
  // because scroll starts at top).
  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] overflow-y-auto"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className={`bg-slate-900 border ${style.border} rounded-xl w-full max-w-md shadow-2xl my-auto`}
        >
          <div className="flex items-start gap-3 p-5 border-b border-slate-800">
            <AlertTriangle className={`w-6 h-6 shrink-0 ${style.icon}`} />
            <div className="flex-1 min-w-0">
              <h2 id="confirm-title" className="font-semibold text-white text-base">
                {title}
              </h2>
            </div>
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 text-sm text-slate-300 leading-relaxed">{message}</div>
          <div className="flex gap-2 p-4 border-t border-slate-800 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800 transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              autoFocus
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${style.button}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
